import React, { useState } from "react";
import { Box, Typography, Paper, Button, Collapse } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import TimelapseRoundedIcon from "@mui/icons-material/TimelapseRounded";
import CancelRoundedIcon from "@mui/icons-material/CancelRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import CommentOutlinedIcon from "@mui/icons-material/CommentOutlined";
import FormatQuoteRoundedIcon from "@mui/icons-material/FormatQuoteRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { BORDER, CORRECTION_STATUSES, CORRECTION_STATUS_ORDER, TEXT_PRIMARY, TEXT_SECONDARY } from "../constants";

const STATUS_ICONS = {
  done: CheckCircleRoundedIcon,
  partially_done: TimelapseRoundedIcon,
  not_done: CancelRoundedIcon,
  needs_review: HelpRoundedIcon,
};
const LONG_TEXT = 220;

export const FilterChip = ({ label, count, active, color = "#334155", onClick }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    aria-pressed={active}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: 0.75,
      px: 1.5,
      py: 0.6,
      borderRadius: 999,
      font: "inherit",
      fontSize: 13,
      fontWeight: 600,
      cursor: "pointer",
      border: `1px solid ${active ? color : BORDER}`,
      bgcolor: active ? `${color}12` : "#fff",
      color: active ? color : TEXT_SECONDARY,
      transition: "all .15s",
      "&:hover, &:focus-visible": { borderColor: color, outline: "none" },
    }}
  >
    {label}
    <Box component="span" sx={{ fontSize: 12, opacity: 0.75 }}>
      {count}
    </Box>
  </Box>
);

export const StatusPill = ({ status }) => {
  const config = CORRECTION_STATUSES[status];
  const Icon = STATUS_ICONS[status];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1,
        py: 0.3,
        borderRadius: 999,
        bgcolor: config.bg,
        color: config.color,
        fontSize: 12.5,
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <Icon sx={{ fontSize: 16 }} />
      {config.label}
    </Box>
  );
};

// One correction as a compact row; the explanation, quote and link to the
// comparison open below it
const CorrectionCard = ({ index, correction, result, open, onToggle, onShowFindings }) => {
  const [fullText, setFullText] = useState(false);
  const config = CORRECTION_STATUSES[result.status];
  const isLong = correction.text.length > LONG_TEXT;
  const text = isLong && !fullText ? `${correction.text.slice(0, LONG_TEXT)}…` : correction.text;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: `1px solid ${open ? `${config.bar}55` : BORDER}`,
        borderInlineStart: `4px solid ${config.bar}`,
        overflow: "hidden",
        transition: "border-color .2s, box-shadow .2s",
        
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          width: "100%",
          px: { xs: 1.5, sm: 2 },
          py: 1.25,
          border: "none",
          bgcolor: "transparent",
          font: "inherit",
          textAlign: "start",
          cursor: "pointer",
          "&:hover": { bgcolor: "#fafbfc" },
          "&:focus-visible": { outline: `2px solid ${config.bar}`, outlineOffset: -2 },
        }}
      >
        <StatusPill status={result.status} />
        <Typography sx={{ fontSize: 13, color: "#94a3b8", fontWeight: 600, flexShrink: 0 }}>#{index + 1}</Typography>
        <Typography dir="auto" noWrap sx={{ flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: TEXT_PRIMARY, textAlign: "start" }}>
          {correction.text.replace(/\s+/g, " ")}
        </Typography>
        {correction.sectionLabel && (
          <Typography sx={{ display: { xs: "none", sm: "block" }, fontSize: 13, color: TEXT_SECONDARY, flexShrink: 0 }}>
            {correction.sectionLabel}
          </Typography>
        )}
        <ExpandMoreRoundedIcon
          sx={{ color: TEXT_SECONDARY, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}
        />
      </Box>

      <Collapse in={open} unmountOnExit>
        <Box sx={{ px: { xs: 1.5, sm: 2 }, pb: 2, pt: 0.5, borderTop: `1px solid ${BORDER}` }}>
          {correction.source === "annotation" && (
            <Box
              component="span"
              sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, fontSize: 13, fontWeight: 600, color: "#7c3aed", mt: 1 }}
            >
              <CommentOutlinedIcon sx={{ fontSize: 14 }} />
              הערה מה-PDF{correction.sectionLabel ? ` · ${correction.sectionLabel}` : ""}
            </Box>
          )}
          <Typography dir="auto" sx={{ fontSize: 15, color: TEXT_PRIMARY, whiteSpace: "pre-wrap", lineHeight: 1.6, mt: 1 }}>
            {text}
          </Typography>
          {isLong && (
            <Button size="small" onClick={() => setFullText((v) => !v)} sx={{ px: 0, minWidth: 0, fontWeight: 600 }}>
              {fullText ? "הצגה מקוצרת" : "הצגת הנוסח המלא"}
            </Button>
          )}

          {result.explanation && (
            <Typography sx={{ fontSize: 15, color: "#334155", mt: 1.25, lineHeight: 1.7 }}>{result.explanation}</Typography>
          )}

          {result.evidence && (
            <Box sx={{ display: "flex", gap: 1, mt: 1.25, p: 1.25, borderRadius: 2, bgcolor: "#f8fafc", border: `1px solid ${BORDER}` }}>
              <FormatQuoteRoundedIcon sx={{ fontSize: 18, color: "#94a3b8", flexShrink: 0, mt: "2px" }} />
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ display: "block", fontSize: 13, color: "#94a3b8", fontWeight: 600 }}>
                  מתוך הגרסה המעודכנת
                </Typography>
                <Typography dir="auto" sx={{ fontSize: 15, color: TEXT_PRIMARY, lineHeight: 1.7 }}>
                  {result.evidence}
                </Typography>
              </Box>
            </Box>
          )}

          {result.findingIds.length > 0 && (
            <Button
              size="small"
              startIcon={<CompareArrowsRoundedIcon />}
              onClick={() => onShowFindings(result.findingIds)}
              sx={{ mt: 1, fontWeight: 600, color: config.color }}
            >
              הצגה בהשוואה ({result.findingIds.length === 1 ? "שינוי אחד" : `${result.findingIds.length} שינויים`})
            </Button>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

/** Table of required corrections with their status (spec 3.4, 3.6) */
const CorrectionsList = ({ check, statusFilter, onStatusFilter, onShowFindings }) => {
  const control = check.correctionsControl;
  const corrections = check.corrections || [];
  // Corrections that need attention start open; done ones start closed
  const [openRows, setOpenRows] = useState(
    () => new Set((control?.results || []).filter((r) => r.status !== "done").map((r) => r.correctionIndex)),
  );
  const toggleRow = (index) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      if (next.has(index)) next.delete(index);
      else next.add(index);
      return next;
    });

  if (!corrections.length) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: `1px dashed ${BORDER}`, textAlign: "center" }}>
        <Typography fontWeight={700} color={TEXT_PRIMARY}>
          לא הוזנה רשימת תיקונים לבדיקה זו
        </Typography>
        <Typography variant="body2" color={TEXT_SECONDARY} sx={{ mt: 0.5 }}>
          בבדיקה הבאה אפשר להזין תיקונים או לייבא אותם מההערות שבקובץ ה-PDF.
        </Typography>
      </Paper>
    );
  }
  if (!control?.results) {
    return (
      <Paper elevation={0} sx={{ p: 4, borderRadius: 2, border: `1px dashed ${BORDER}`, textAlign: "center" }}>
        <Typography color={TEXT_SECONDARY}>
          {corrections.length} תיקונים הוזנו — ניתן להריץ את בקרת התיקונים מהסיכום שלמעלה.
        </Typography>
      </Paper>
    );
  }

  const rows = control.results
    .map((result) => ({ result, correction: corrections[result.correctionIndex], index: result.correctionIndex }))
    .filter(({ correction }) => correction)
    .sort(
      (p, q) =>
        CORRECTION_STATUS_ORDER.indexOf(p.result.status) - CORRECTION_STATUS_ORDER.indexOf(q.result.status) ||
        p.index - q.index,
    );
  const visible = rows.filter(({ result }) => statusFilter === "all" || result.status === statusFilter);
  const allOpen = visible.length > 0 && visible.every(({ index }) => openRows.has(index));
  const setAll = (open) =>
    setOpenRows((prev) => {
      const next = new Set(prev);
      visible.forEach(({ index }) => (open ? next.add(index) : next.delete(index)));
      return next;
    });

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
        <FilterChip label="הכל" count={rows.length} active={statusFilter === "all"} onClick={() => onStatusFilter("all")} />
        {CORRECTION_STATUS_ORDER.filter((status) => control.summary[status] > 0).map((status) => (
          <FilterChip
            key={status}
            label={CORRECTION_STATUSES[status].label}
            count={control.summary[status]}
            color={CORRECTION_STATUSES[status].color}
            active={statusFilter === status}
            onClick={() => onStatusFilter(status)}
          />
        ))}
        <Box sx={{ flex: 1 }} />
        <Button size="small" onClick={() => setAll(!allOpen)} sx={{ fontWeight: 600, color: TEXT_SECONDARY }}>
          {allOpen ? "סגירת הכל" : "פתיחת הכל"}
        </Button>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {visible.map(({ result, correction, index }) => (
          <CorrectionCard
            key={index}
            index={index}
            correction={correction}
            result={result}
            open={openRows.has(index)}
            onToggle={() => toggleRow(index)}
            onShowFindings={onShowFindings}
          />
        ))}
      </Box>
    </Box>
  );
};

export default CorrectionsList;
